import { Fragment, useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Info from '../../components/Info';
import DateFormat from '../../components/DateFormat';
import Price, { CURRENCY } from '../../components/Price';
import Phone from '../../components/Phone';
import RepositoryModel from '../../components/RepositoryModel';
import { RepositoryContext } from '../../store/RepositoryProvider';
import Select from '../../components/Select';
import InputAutocomplete from '../../components/InputAutocomplete';
import API from '../../server/api';
import log from '../../utils/logger';
import Button, { BUTTON_POSITION, BUTTON_TYPE } from '../../components/Button';
import Alert, { ALERT_TYPE } from '../../components/Alert';
import Modal from '../../components/Modal';
import BackBar from '../../components/BackBar';
import { OrderStatus, PaymentType } from '../../models/types';
import PayOrder from './pay_order';
import { confirm } from '../../components/Confirm';
import { env } from '../../utils/util';
import { notify } from '../../components/Toast';
import Field from '../../components/Field';

const ticketHost = env('REACT_APP_TRIP_COMPANY_TICKET_SERVER_URL', '').trim();

const OrderInfo = ({order, onClose = () => {}}) => {
  const {t} = useTranslation(['common', 'sale', 'order']);

  const ticketUrl = `${ticketHost}${ticketHost.endsWith('/')?'':'/'}${order.id}`;

  const repository = useContext(RepositoryContext);

  const [places, setPlaces] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [errors, setErrors] = useState([]);

  const [place, setPlace] = useState(order.place ? order.place.id : '');
  const [address, setAddress] = useState(order.address);
  const [comment, setComment] = useState(order.comment);

  const [showPayOrder, setShowPayOrder] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const handleClosePayOrder = useCallback(() => {
    setShowPayOrder(false);
  }, []);

  const handleOpenPayOrder = useCallback(() => {
    setShowPayOrder(true);
  }, []);

  const handleCancelOrder = useCallback(async () => {
    if ((await confirm(t('order:new_order.confirm_cancel'), t('common:button.yes'), t('common:button.no')))) {
      repository.request(API.order.cancel, { orderId: order.id}).then(() => {
        order.status = OrderStatus.CANCELLED;
      }).catch((e) => {
        console.error(e);
      });
    }
  }, []);


  const handleCloseError = (uid) => {
    setErrors(errors.filter((e) => e.uid !== uid));
  };

  const updateOrder = () => {
    let data = {};

    if (order.place) {
      data.place = place;
    }

    if (order.address) {
      data.address = address;
    }

    data.comment = comment;

    setIsLoading(true);

    repository.request(API.order.update, { id: order.id, ...data }).then((o) => {
      setIsLoading(false);
      order.place = o.place;
      order.address = o.address;
      order.comment = o.comment;
      onClose && onClose();
    }).catch((e) => {
      setIsLoading(false);
      log.error(e);
      let errors = [];
      if (e.error && e.error.toLowerCase() === 'form_error') {
        for (let field in e.fields) {
          errors.push({ code: 'form_error', type: ALERT_TYPE.ERROR, title: t('order:error.form_error'), message: t(`order:error.${field}.${e.fields[field]}`) });
        }
      }
      setErrors(errors);
    });
  }

  const handleCopyTicket = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(ticketUrl);
      notify.success(t('common:labels.copied'));
    } catch (e) {
      log.error('Failed to copy text: ', e);
    }
  }, []);

  const isChanged = useCallback(() => {
    return (
      (comment === order.comment) &&
      (!order.address || address === order.address) &&
      (!order.place || place === order.place.id)
    );
  }, [address, place, comment]);

  useEffect(() => {
    if (address) {
      repository.list(API.geo.address, { address }, Object).then((addresses) => {
        setAddresses(addresses ? addresses[1].map((address) => address.name) : []);
        return addresses;
      }).catch((e) => {
        log.error(e);
        setAddresses([]);
        return [];
      });
    } else {
      setAddresses([]);
    }
  }, [address]);

  useEffect(() => {
    repository.request(API.schedule.schedule, { id: order?.schedule?.id || order.orderPostponed?.schedule?._id }).then((schedule) => {
      const places = schedule.getPlaces().map((place) => ({ value: place.place.id, text: place.titleTime }));
      setPlaces(places);
      const newPlace = schedule.getPlaces().find((place) => order.place && place.place.id === order.place.id);
      setPlace(newPlace ? newPlace.place.id : (schedule.getPlaces().length > 0 ? schedule.getPlaces()[0].place.id : ''));
    });
  }, []);

  return (
    <div className="trip-app">
      <div className="trip-app-body">

        <Button.Panel>
          <Button variant={BUTTON_TYPE.SECONDARY} onClick={handleCopyTicket}>{t('order:order_info.copy_link')}</Button>
        </Button.Panel>

        {(errors.length > 0) && errors.map((error, i) => (
          <Alert type={error.type} key={error.uid || i} title={error.title} dismissible={true} onClose={() => handleCloseError(error.uid)}>{error.message}</Alert>
        ))}

        <Info title={t('order:order_info.excursion')}>
          {order.excursion.title}
        </Info>
        <Info title={t('order:order_info.departure')}>
          <DateFormat.DateTime date={order?.schedule?.date}/>
        </Info>
        <Info title={t('order:order_info.client')}>
          <div>{order.client.fullName}</div>
          <div><Phone.Call value={order.client.phone}/></div>
        </Info>
        <Info title={t('order:order_info.passengers')}>
          {order.passengers.map((passenger) => (
            <div key={passenger.id}>
              {passenger.count} <RepositoryModel type="Price" uid={passenger.price.id} render={(model) => model.title} />
            </div>
          ))}
        </Info>
        <Info title={t('order:order_info.additional')}>
          {order.wishes.map((wish, i) => (
            <span className="comma" key={i}>{t(`common:wishes.${wish}`)}</span>
          ))}
        </Info>
        <Info title={t(`sale:status.${order.status}`)}>
          {(order.status !== OrderStatus.PAID) && (
            <>
              <Price currency={CURRENCY.RUB} value={order.paymentDetails.partialValue || order.paymentDetails.amount} /> - {t(`common:payment_method.${order.paymentDetails.method}`)}
            </>
          )}
          {(order.status === OrderStatus.PAID) && (
            <>
              {order.paymentDetails.payments.filter((payment) => payment.type === PaymentType.INCOME).map((payment, i) => (
                <Fragment key={i}>
                  <Price currency={CURRENCY.RUB} value={payment.partialValue || payment.amount} /> - {t(`common:payment_method.${payment.method}`)}
                </Fragment>
              ))}
            </>
          )}
          {order.status === OrderStatus.AWAITING_PAYMENT && (
            <Button.Panel>
              <Button position={BUTTON_POSITION.RIGHT} variant={BUTTON_TYPE.PRIMARY} onClick={handleCancelOrder}>{t('order:order_info.cancel_order')}</Button>
              <Button position={BUTTON_POSITION.LEFT} variant={BUTTON_TYPE.PRIMARY} onClick={handleOpenPayOrder}>{t('order:order_info.pay_order')}</Button>
            </Button.Panel>
          )}
        </Info>
        {order.paymentDetails.leftToPay > 0 && (
          <Info title={t('order:order_info.left_to_pay')}>
            <Price currency={CURRENCY.RUB} value={order.paymentDetails.leftToPay} />
          </Info>
        )}
        <Info title={t('order:order_info.seats')}>
          {(order.newSeats?.length ? order.newSeats : order.seats).map((transport, i) => (
            <div key={i}>
                {!!transport.transport?.title && (
                    <>
                        <span className="me-1">{transport.transport?.number}: {transport.transport?.title} -</span>
                    </>
                )}
                <span>{transport.userSeats.length > 0 ? transport.userSeats.join(', ') : t('order:order_info.seats')}</span>
            </div>
          ))}
        </Info>

        {order.place && (
          <Field title={t('order:order_info.place')}>
            <Select
              value={place}
              onSelect={setPlace}
              empty={{ value: -1, text: t('order:common_info.none')}}
              options={places}
            />
          </Field>
        )}
        {order.address && (
          <Field title={t('order:order_info.address')}>
            <InputAutocomplete
              list={addresses}
              value={address}
              onInput={setAddress}
              asisText={false}
            />
          </Field>
        )}

        <Field title={t('order:order_info.comment')}>
          <textarea rows="3" onChange={(e) => setComment(e.target.value)} value={comment}/>
        </Field>

        <br/>
      </div>
      <div className="trip-app-footer">
        <Button.Panel>
          <Button position={BUTTON_POSITION.LEFT} disabled={isLoading || isChanged()} onClick={updateOrder}>{t('common:button.save')}</Button>
          <Button position={BUTTON_POSITION.RIGHT} variant={BUTTON_TYPE.SECONDARY} onClick={onClose}>{t('common:button.close')}</Button>
        </Button.Panel>
      </div>

      <Modal show={showPayOrder} onHide={handleClosePayOrder}>
        <Modal.Header closeButton>
          <BackBar title={t('order:order_info.title', { order: order.number })}/>
        </Modal.Header>
        <Modal.Body>
          {showPayOrder && ( /* re-create body*/
            <PayOrder order={order} onClose={handleClosePayOrder}/>
          )}
        </Modal.Body>
      </Modal>
    </div>
  )
}

OrderInfo.propTypes = {
  order: PropTypes.object.isRequired
};

export default OrderInfo;